import React, { useState, useEffect } from 'react';
import { loadConfig, API_BASE_URL } from '@config';
import FuseSplashScreen from '@fuse/components/FuseSplashScreen/FuseSplashScreen';

const ConfigProvider = ({ children }) => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;

  useEffect(() => {
    const loadAppConfig = async () => {
      try {
        await loadConfig();
        
        // Verify we have valid API_BASE_URL after loading config
        if (!API_BASE_URL && retryCount < maxRetries) {
          console.warn(`ConfigProvider: Config loaded but API_BASE_URL is still undefined. Retry ${retryCount + 1}/${maxRetries}`);
          setRetryCount(prev => prev + 1);
          // Wait longer between retries
          const delay = Math.min(1000 * Math.pow(1.5, retryCount), 10000); 
          setTimeout(loadAppConfig, delay);
          return;
        }
        
        setConfigLoaded(true);
      } catch (err) {
        console.error('ConfigProvider: Failed to load configuration', err);
        
        if (retryCount < maxRetries) {
          console.warn(`ConfigProvider: Retrying config load. Attempt ${retryCount + 1}/${maxRetries}`);
          setRetryCount(prev => prev + 1);
          // Wait longer between retries
          const delay = Math.min(1000 * Math.pow(1.5, retryCount), 10000);
          setTimeout(loadAppConfig, delay);
        } else {
          setError(err);
          // Even with error, we continue with fallback URL
          console.log('ConfigProvider: Using fallback URL for API_BASE_URL');
          setConfigLoaded(true);
        }
      }
    };

    loadAppConfig();
  }, [retryCount, maxRetries]);

  if (error) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h2 style={{ color: '#f44336', marginBottom: '16px' }}>Configuration Error</h2>
        <p style={{ marginBottom: '16px' }}>The application could not load its configuration properly.</p>
        <p style={{ marginBottom: '24px' }}>Using fallback configuration settings. Some features may be limited.</p>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'left',
          overflow: 'auto'
        }}>
          <pre style={{ margin: 0 }}>{error.message}</pre>
        </div>
        <button 
          onClick={() => {
            setRetryCount(0);
            setError(null);
          }}
          style={{
            marginTop: '24px',
            padding: '8px 16px',
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Retry Loading Configuration
        </button>
      </div>
    );
  }

  if (!configLoaded) {
    return <FuseSplashScreen />;
  }

  return children;
};

export default ConfigProvider;
